import React, { useEffect, useState, useMemo } from 'react';
import { Tabs, Spin, Alert, Card, Modal, Descriptions, Button, Form, Input, Select, DatePicker } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Layout from '../../components/Layout';
import AntTable from '../../components/CustomTable/AntTable';
import AntDropdown from '../../components/CustomDropdown/AntDropdown';
import { customBaseUrl, httpGet, httpPost } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import { render } from 'less';

const { TabPane } = Tabs;

const RefreshBvnForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        dateOfBirth: values.dateOfBirth ? dayjs(values.dateOfBirth).format('YYYY-MM-DD') : undefined,
      };
      const res = await httpPost(
        `${customBaseUrl.authUrl}/auth/bvn/refresh`,
        payload
      );
      if (res.success) {
        NotificationManager.success(res.message || 'BVN refreshed successfully!');
        form.resetFields();
      } else {
        NotificationManager.error(res.message || 'Failed to refresh BVN.');
      }
    } catch (error) {
      console.error('Error refreshing BVN:', error);
      NotificationManager.error('An error occurred while refreshing BVN.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Refresh BVN Data">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
      >
        <Form.Item
          name="bvn"
          label="BVN"
          rules={[{ required: true, message: 'Please enter the BVN.' }, { len: 11, message: 'BVN must be 11 digits.' }]}
        >
          <Input placeholder="Enter 11-digit BVN" />
        </Form.Item>
        <Form.Item
          name="dateOfBirth"
          label="Date of Birth"
          rules={[{ required: true, message: 'Please select date of birth!' }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="processType"
          label="Process Type"
          rules={[{ required: true, message: 'Please select a process type.' }]}
        >
          <Select placeholder="Select a process type">
            <Select.Option value="ADVANCE">ADVANCE</Select.Option>
            <Select.Option value="BASIC">BASIC</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Refresh BVN
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

// ── Dual-backend helpers (mirrors nmifos wayabank-api.config.ts) ────────────
// Reads the same localStorage key that nmifos writes when the user picks a backend.
// 'cba'      → direct CBA micro-service URLs (existing customBaseUrl.*)
// 'fineract' → Kong API Gateway  (https://kong-api.staging.wayabank.ng/apis)
const getBackendMode = () =>
  (localStorage.getItem('waya_backend_mode') || 'cba');

// Kong base URL — prefer an explicit env var, fall back to the staging gateway.
const KONG_BASE =
  process.env.REACT_APP_KONG_URL ||
  'https://kong-api.staging.wayabank.ng/apis';

const ManageKyc = () => {
  const [activeTab, setActiveTab] = useState('nin');

  // NIN Verification State
  const [ninVerifications, setNinVerifications] = useState([]);
  const [ninLoading, setNinLoading] = useState(false);
  const [ninError, setNinError] = useState(null);
  const [ninPagination, setNinPagination] = useState({
    current: 0,
    pageSize: 10,
  });

  // Utility Verification State
  const [utilityVerifications, setUtilityVerifications] = useState([]);
  const [utilityLoading, setUtilityLoading] = useState(false);
  const [utilityError, setUtilityError] = useState(null);
  const [utilityPagination, setUtilityPagination] = useState({
    current: 0,
    pageSize: 10,
  });

  // CAC Verification State
  const [cacVerifications, setCacVerifications] = useState([]);
  const [cacLoading, setCacLoading] = useState(false);
  const [cacError, setCacError] = useState(null);
  const [cacPagination, setCacPagination] = useState({
    current: 0,
    pageSize: 10,
  });

  // Search Filtering State
  const [ninSearchText, setNinSearchText] = useState('');
  const [utilitySearchText, setUtilitySearchText] = useState('');
  const [utilityStatus, setUtilityStatus] = useState('PENDING');
  const [ninStatus, setNinStatus] = useState('PENDING');
  const [ninApprovedBy, setNinApprovedBy] = useState('');
  const [ninFromDate, setNinFromDate] = useState(null);
  const [ninToDate, setNinToDate] = useState(null);
  const [utilityApprovedBy, setUtilityApprovedBy] = useState('');
  const [utilityFromDate, setUtilityFromDate] = useState(null);
  const [utilityToDate, setUtilityToDate] = useState(null);
  const [cacStatus, setCacStatus] = useState('PENDING');
  const [cacSearchText, setCacSearchText] = useState('');
  const [cacApprovedBy, setCacApprovedBy] = useState('');
  const [cacFromDate, setCacFromDate] = useState(null);
  const [cacToDate, setCacToDate] = useState(null);

  const filteredNinVerifications = useMemo(() => {
    if (!ninSearchText) return ninVerifications;
    const q = ninSearchText.toLowerCase();
    return ninVerifications.filter(item => {
      const fullName = `${item.firstName || ''} ${item.lastName || ''}`.toLowerCase();
      const email = (item.email || '').toLowerCase();
      const nin = (item.nin || '').toLowerCase();
      const phone = (item.phoneNumber || item.userPhoneNumber || '').toLowerCase();
      return fullName.includes(q) || email.includes(q) || nin.includes(q) || phone.includes(q);
    });
  }, [ninVerifications, ninSearchText]);

  const filteredUtilityVerifications = useMemo(() => {
    if (!utilitySearchText) return utilityVerifications;
    const q = utilitySearchText.toLowerCase();
    return utilityVerifications.filter(item => {
      const fullName = `${item.userFirstName || ''} ${item.userLastName || ''}`.toLowerCase();
      const email = (item.email || '').toLowerCase();
      const accNo = (item.accountNumber || item.userAccountNumber || '').toLowerCase();
      const phone = (item.userPhoneNumber || '').toLowerCase();
    });
  }, [utilityVerifications, utilitySearchText]);

  const filteredCacVerifications = useMemo(() => {
    if (!cacSearchText) return cacVerifications;
    const q = cacSearchText.toLowerCase();
    return cacVerifications.filter(item => {
      const bizName = (item.businessName || '').toLowerCase();
      const regNo = (item.registrationNumber || '').toLowerCase();
      const email = (item.email || '').toLowerCase();
      const phone = (item.phoneNo || '').toLowerCase();
      return bizName.includes(q) || regNo.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [cacVerifications, cacSearchText]);

  // Utility Modal State
  const [isUtilityModalVisible, setIsUtilityModalVisible] = useState(false);
  const [selectedUtilityRecord, setSelectedUtilityRecord] = useState(null);
  const [isUtilityActionLoading, setIsUtilityActionLoading] = useState(false);

  // Document Authenticity Verification (keyed by 'utility' | 'additional')
  const [docVerifyLoading, setDocVerifyLoading] = useState({ utility: false, additional: false });
  const [docVerifyResult, setDocVerifyResult] = useState({ utility: null, additional: null });
  const [docVerifyError, setDocVerifyError] = useState({ utility: null, additional: null });

  // Meter Validation State
  const [meterValidationResult, setMeterValidationResult] = useState(null);
  const [isMeterValidating, setIsMeterValidating] = useState(false);
  const [meterValidationError, setMeterValidationError] = useState(null);
  const [electricityProviders, setElectricityProviders] = useState([]);
  const [manualMeterNo, setManualMeterNo] = useState('');
  const [manualServiceType, setManualServiceType] = useState('');

  // CAC Modal State
  const [isCacModalVisible, setIsCacModalVisible] = useState(false);
  const [selectedCacRecord, setSelectedCacRecord] = useState(null);
  const [isCacActionLoading, setIsCacActionLoading] = useState(false);

  // Geolocation state
  const [resolvedAddress, setResolvedAddress] = useState('');
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);
  const [resolveAddressError, setResolveAddressError] = useState(null);

  // Billing Address Geocoding state
  const [billingAddressGeocode, setBillingAddressGeocode] = useState(null);
  const [isGeocodingBillingAddress, setIsGeocodingBillingAddress] = useState(false);
  const [billingAddressGeocodeError, setBillingAddressGeocodeError] = useState(null);

  // Backend Location Check comparison state
  const [backendComparisonResult, setBackendComparisonResult] = useState(null);
  const [isBackendComparing, setIsBackendComparing] = useState(false);
  const [backendComparisonError, setBackendComparisonError] = useState(null);

  const fetchNinVerifications = async (statusVal = ninStatus, params = {}, isReset = false) => {
    setNinLoading(true);
    try {
      const page = params.pagination?.current ? params.pagination.current - 1 : 0;
      const size = params.pagination?.pageSize || ninPagination.pageSize || 20;

      let url = `${customBaseUrl.authUrl}/api/v1/pending-nin-verifications/search?status=${statusVal}&page=${page}&size=${size}&sortBy=createdAt&sortDir=desc`;

      if (!isReset) {
        if (ninApprovedBy) {
          url += `&approvedBy=${encodeURIComponent(ninApprovedBy)}`;
        }
        if (ninFromDate) {
          url += `&fromDate=${dayjs(ninFromDate).format('YYYY-MM-DD')}`;
        }
        if (ninToDate) {
          url += `&toDate=${dayjs(ninToDate).format('YYYY-MM-DD')}`;
        }
      }

      const res = await httpGet(url);
      if (res?.success === true) {
        const list = res.data?.content || (Array.isArray(res.data) ? res.data : []);
        const total = res.data?.totalElements || res.data?.length || list.length || 0;
        setNinVerifications(list);
        setNinPagination((prev) => ({
          ...prev,
          current: page + 1,
          pageSize: size,
          total: total,
        }));
      } else if (res && (res.content || Array.isArray(res))) {
        const list = res.content || (Array.isArray(res) ? res : []);
        const total = res.totalElements || res.length || list.length || 0;
        setNinVerifications(list);
        setNinPagination((prev) => ({
          ...prev,
          current: page + 1,
          pageSize: size,
          total: total,
        }));
      } else {
        setNinVerifications([]);
        setNinError(res?.message || 'Failed to fetch NIN verifications.');
      }
    } catch (err) {
      setNinVerifications([]);
      setNinError('An error occurred while fetching NIN verifications.');
      console.error(err);
    } finally {
      setNinLoading(false);
    }
  };

  const fetchUtilityVerifications = async (statusVal = utilityStatus, params = {}, isReset = false) => {
    setUtilityLoading(true);
    try {
      const page = params.pagination?.current ? params.pagination.current - 1 : 0;
      const size = params.pagination?.pageSize || utilityPagination.pageSize || 20;

      let url = `${customBaseUrl.authUrl}/api/v1/utility-bill-verifications/search?status=${statusVal}&page=${page}&size=${size}&sortBy=createdAt&sortDir=desc`;

      if (!isReset) {
        if (utilityApprovedBy) {
          url += `&approvedBy=${encodeURIComponent(utilityApprovedBy)}`;
        }
        if (utilityFromDate) {
          url += `&fromDate=${dayjs(utilityFromDate).format('YYYY-MM-DD')}`;
        }
        if (utilityToDate) {
          url += `&toDate=${dayjs(utilityToDate).format('YYYY-MM-DD')}`;
        }
      }

      const res = await httpGet(url);
      if (res?.success === true) {
        const list = res.data?.content || (Array.isArray(res.data) ? res.data : []);
        const total = res.data?.totalElements || res.data?.length || list.length || 0;
        setUtilityVerifications(list);
        setUtilityPagination((prev) => ({
          ...prev,
          current: page + 1,
          pageSize: size,
          total: total,
        }));
      } else if (res && (res.content || Array.isArray(res))) {
        const list = res.content || (Array.isArray(res) ? res : []);
        const total = res.totalElements || res.length || list.length || 0;
        setUtilityVerifications(list);
        setUtilityPagination((prev) => ({
          ...prev,
          current: page + 1,
          pageSize: size,
          total: total,
        }));
      } else {
        setUtilityVerifications([]);
        setUtilityError(res?.message || 'Failed to fetch utility verifications.');
      }
    } catch (err) {
      setUtilityVerifications([]);
      setUtilityError('An error occurred while fetching utility verifications.');
      console.error(err);
    } finally {
      setUtilityLoading(false);
    }
  };

  const fetchCacVerifications = async (statusVal = cacStatus, params = {}, isReset = false) => {
    setCacLoading(true);
    try {
      const page = params.pagination?.current ? params.pagination.current - 1 : 0;
      const size = params.pagination?.pageSize || cacPagination.pageSize || 20;

      let url = `${customBaseUrl.authUrl}/api/v1/cac-business-accounts/search?status=${statusVal}&page=${page}&size=${size}&sortBy=createdAt&sortDir=desc`;

      if (!isReset) {
        if (cacApprovedBy) {
          url += `&approvedBy=${encodeURIComponent(cacApprovedBy)}`;
        }
        if (cacFromDate) {
          url += `&fromDate=${dayjs(cacFromDate).format('YYYY-MM-DD')}`;
        }
        if (cacToDate) {
          url += `&toDate=${dayjs(cacToDate).format('YYYY-MM-DD')}`;
        }
      }

      const res = await httpGet(url);
      if (res?.success === true) {
        const list = res.data?.content || (Array.isArray(res.data) ? res.data : []);
        const total = res.data?.totalElements || res.data?.length || list.length || 0;
        setCacVerifications(list);
        setCacPagination((prev) => ({
          ...prev,
          current: page + 1,
          pageSize: size,
          total: total,
        }));
      } else if (res && (res.content || Array.isArray(res))) {
        const list = res.content || (Array.isArray(res) ? res : []);
        const total = res.totalElements || res.length || list.length || 0;
        setCacVerifications(list);
        setCacPagination((prev) => ({
          ...prev,
          current: page + 1,
          pageSize: size,
          total: total,
        }));
      } else {
        setCacVerifications([]);
        setCacError(res?.message || 'Failed to fetch CAC verifications.');
      }
    } catch (err) {
      setCacVerifications([]);
      setCacError('An error occurred while fetching CAC verifications.');
      console.error(err);
    } finally {
      setCacLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'nin') {
      fetchNinVerifications(ninStatus);
    } else if (activeTab === 'utility') {
      fetchUtilityVerifications(utilityStatus);
    } else if (activeTab === 'cac') {
      fetchCacVerifications(cacStatus);
    }
  }, [activeTab, utilityStatus, ninStatus, cacStatus]);

  const handleNinAction = async (action, record) => {
    if (action === 'View Details') {
      Swal.fire({
        title: 'Fetching NIN Details...',
        didOpen: () => {
          Swal.showLoading();
        },
        allowOutsideClick: false,
      });

      try {
        const res = await httpPost(`${customBaseUrl.authUrl}/api/v1/pending-nin-verifications/verify-virtual-nin?userId=${record.userId}`);
        Swal.close();

        if (res.success && res.data) {
          const capturedPhotoUrl = res.data.capturedPhotoUrl;
          const ninPhotoBase64 = res.data.verificationResult.nin_data.photo;

          const comparisonHtml = `
            <div style="display: flex; justify-content: space-around; align-items: center; text-align: center;">
              <div>
                <h3>Captured Photo</h3>
                <img src="${capturedPhotoUrl}" alt="Captured Photo" style="max-width: 250px; max-height: 250px; border: 1px solid #ddd;"/>
              </div>
              <div>
                <h3>NIN Photo</h3>
                <img src="data:image/jpeg;base64,${ninPhotoBase64}" alt="NIN Photo" style="max-width: 250px; max-height: 250px; border: 1px solid #ddd;"/>
              </div>
            </div>
          `;

          Swal.fire({
            title: 'Compare Photos',
            html: comparisonHtml,
            showDenyButton: true,
            showCancelButton: true,
            confirmButtonText: 'Approve',
            denyButtonText: 'Reject',
            width: '700px',
          }).then((result) => {
            if (result.isConfirmed || result.isDenied) {
              const modalAction = result.isConfirmed ? 'Approve' : 'Reject';

              Swal.fire({
                title: `Enter remarks for ${modalAction}ing face verification`,
                input: 'text',
                inputAttributes: { autocapitalize: 'off' },
                showCancelButton: true,
                confirmButtonText: 'Submit',
                showLoaderOnConfirm: true,
                preConfirm: (remarks) => {
                  if (!remarks) {
                    Swal.showValidationMessage('Remarks cannot be empty!');
                  }
                  return remarks;
                },
                allowOutsideClick: () => !Swal.isLoading()
              }).then(async (remarksResult) => {
                if (remarksResult.isConfirmed) {
                  const remarks = encodeURIComponent(remarksResult.value);
                  try {
                    let approveRejectRes;
                    if (modalAction === 'Approve') {
                      approveRejectRes = await httpPost(`${customBaseUrl.authUrl}/api/v1/pending-nin-verifications/${record.id}/approve?remarks=${remarks}`);
                    } else {
                      approveRejectRes = await httpPost(`${customBaseUrl.authUrl}/api/v1/pending-nin-verifications/${record.id}/reject?remarks=${remarks}`);
                    }

                    if (approveRejectRes?.success) {
                      NotificationManager.success(`${modalAction}d NIN face verification successfully!`);
                      fetchNinVerifications();
                    } else {
                      NotificationManager.error(approveRejectRes?.error?.message || approveRejectRes?.message || `Failed to ${modalAction} NIN verification.`);
                    }
                  } catch (err) {
                    NotificationManager.error(`An error occurred while ${modalAction}ing NIN face verification.`);
                    console.error(err);
                  }
                }
              });
            }
          });
        } else {
          NotificationManager.error(res?.error?.message || res?.message || 'Failed to fetch NIN details.');
        }
      } catch (err) {
        Swal.close();
        NotificationManager.error('An error occurred while fetching NIN details.');
        console.error(err);
      }
    } else { // This is the original logic for Approve/Reject from the dropdown
      Swal.fire({
        title: `Enter remarks for ${action}ing NIN face verification for ${record.firstName} ${record.lastName}`,
        input: 'text',
        inputAttributes: {
          autocapitalize: 'off'
        },
        showCancelButton: true,
        confirmButtonText: 'Submit',
        showLoaderOnConfirm: true,
        preConfirm: (remarks) => {
          if (!remarks) {
            Swal.showValidationMessage(
              'Remarks cannot be empty!'
            );
          }
          return remarks;
        },
        allowOutsideClick: () => !Swal.isLoading()
      }).then(async (result) => {
        if (result.isConfirmed) {
          const remarks = encodeURIComponent(result.value);
          try {
            let res;
            if (action === 'Approve') {
              res = await httpPost(`${customBaseUrl.authUrl}/api/v1/pending-nin-verifications/${record.id}/approve?remarks=${remarks}`);
            } else if (action === 'Reject') {
              res = await httpPost(`${customBaseUrl.authUrl}/api/v1/pending-nin-verifications/${record.id}/reject?remarks=${remarks}`);
            }

            if (res?.success) {
              NotificationManager.success(`${action}d NIN verification successfully!`);
              fetchNinVerifications(); // Refresh the list
            } else {
              NotificationManager.error(res?.error?.message || res?.message || `Failed to ${action} NIN verification.`);
            }
          } catch (err) {
            NotificationManager.error(`An error occurred while ${action}ing NIN verification.`);
            console.error(err);
          }
        }
      });
    }
  };



  const geocodeBillingAddress = async (address, recordId) => {
    if (!address) return;
    setIsGeocodingBillingAddress(true);
    setBillingAddressGeocode(null);
    setBillingAddressGeocodeError(null);
    try {
      // Try Nominatim search first
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`, {
        headers: {
          'Accept-Language': 'en'
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          setBillingAddressGeocode({ lat, lon, display_name: data[0].display_name });
          if (recordId) {
            compareLocationWithBackend(recordId, lat, lon);
          }
          return;
        }
      }

      // Fallback: Photon
      const fallbackRes = await fetch(`https://photon.komoot.io/api?q=${encodeURIComponent(address)}&limit=1`);
      if (fallbackRes.ok) {
        const data = await fallbackRes.json();
        if (data && data.features && data.features.length > 0) {
          const lon = parseFloat(data.features[0].geometry.coordinates[0]);
          const lat = parseFloat(data.features[0].geometry.coordinates[1]);
          const props = data.features[0].properties;
          const display_name = [
            props.name,
            props.street,
            props.city,
            props.state,
            props.country
          ].filter(Boolean).join(', ');
          setBillingAddressGeocode({ lat, lon, display_name });
          if (recordId) {
            compareLocationWithBackend(recordId, lat, lon);
          }
          return;
        }
      }

      setBillingAddressGeocodeError('Could not resolve billing address to coordinates.');
    } catch (err) {
      console.error('Error geocoding billing address:', err);
      setBillingAddressGeocodeError('Error fetching coordinates for billing address.');
    } finally {
      setIsGeocodingBillingAddress(false);
    }
  };

  const compareLocationWithBackend = async (id, refLat, refLng) => {
    setIsBackendComparing(true);
    setBackendComparisonResult(null);
    setBackendComparisonError(null);
    try {
      const res = await httpGet(
        `${customBaseUrl.authUrl}/api/v1/utility-bill-verifications/${id}/location/check?refLat=${refLat}&refLng=${refLng}`
      );
      if (res?.success) {
        setBackendComparisonResult(res.data);
      } else {
        setBackendComparisonError(res?.message || res?.error?.message || 'Backend comparison failed.');
      }
    } catch (err) {
      console.error('Error in backend location comparison:', err);
      setBackendComparisonError('An error occurred during backend location comparison.');
    } finally {
      setIsBackendComparing(false);
    }
  };

  const resolveGeolocationAddress = async (lat, lon) => {
    if (!lat || !lon) return;
    setIsResolvingAddress(true);
    setResolvedAddress('');
    setResolveAddressError(null);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`, {
        headers: {
          'Accept-Language': 'en'
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.display_name) {
          setResolvedAddress(data.display_name);
          return;
        }
      }

      // Fallback
      const fallbackRes = await fetch(`https://photon.komoot.io/reverse?lat=${lat}&lon=${lon}`);
      if (fallbackRes.ok) {
        const data = await fallbackRes.json();
        if (data && data.features && data.features.length > 0) {
          const props = data.features[0].properties;
          const parts = [
            props.name,
            props.street,
            props.city,
            props.state,
            props.country
          ].filter(Boolean);
          if (parts.length > 0) {
            setResolvedAddress(parts.join(', '));
            return;
          }
        }
      }
      setResolveAddressError('Could not resolve geolocation to a physical address.');
    } catch (err) {
      console.error('Error resolving geolocation address:', err);
      setResolveAddressError('Error fetching geolocation address.');
    } finally {
      setIsResolvingAddress(false);
    }
  };

  const fetchElectricityProviders = async (record) => {
    try {
      const isCba = getBackendMode() === 'cba';
      const providersUrl = isCba
        ? `${customBaseUrl.tranv2}/api/v1/wayabills/electricity/providers`
        : `${KONG_BASE}/v1/cba/waya/bills/electricity/providers`;

      const providerRes = await httpGet(providersUrl);

      // CBA returns: { data: { data: [...] } }
      // Kong returns: { data: [...] }  (normalise both)
      const providers =
        providerRes?.data?.data ||
        (Array.isArray(providerRes?.data) ? providerRes.data : []);

      setElectricityProviders(providers);

      // Pre-select the provider that matches the record
      const providerName = (record?.utilityProvider || '').toLowerCase().trim();
      const matched = providers.find(
        (p) =>
          (p.name || '').toLowerCase().trim() === providerName ||
          (p.shortname || '').toLowerCase().trim() === providerName ||
          (p.service_type || '').toLowerCase().trim() === providerName
      );
      if (matched) setManualServiceType(matched.service_type);
    } catch (err) {
      console.error('Failed to fetch electricity providers:', err);
    }
  };

  const validateMeterNumber = async (meterNo, serviceType) => {
    if (!meterNo || !serviceType) {
      NotificationManager.warning('Please select a provider and enter a meter number.');
      return;
    }
    setIsMeterValidating(true);
    setMeterValidationResult(null);
    setMeterValidationError(null);
    try {
      const isCba = getBackendMode() === 'cba';
      const validateUrl = isCba
        ? `${customBaseUrl.tranv2}/api/v1/wayabills/validate`
        : `${KONG_BASE}/v1/cba/waya/bills/validate`;

      const validateRes = await httpPost(
        validateUrl,
        { service_type: serviceType, account_number: meterNo }
      );

      // Normalise response shapes:
      // CBA:  { success, data: { data: { user: { name, address, accountNumber } } } }
      // Kong: { success, data: { user: { name, address, accountNumber } } }  (may vary)
      const user =
        validateRes?.data?.data?.user ||
        validateRes?.data?.user ||
        null;

      if (validateRes?.success && user) {
        setMeterValidationResult(user);
      } else {
        setMeterValidationError(
          validateRes?.message ||
          validateRes?.data?.message ||
          'Meter validation failed.'
        );
      }
    } catch (err) {
      console.error('Meter validation error:', err);
      setMeterValidationError('An error occurred during meter validation.');
    } finally {
      setIsMeterValidating(false);
    }
  };

  const handleVerifyDocumentAuthenticity = async (kind, documentType = 'UTILITY_BILL') => {
    if (!selectedUtilityRecord) return;
    const documentUrl = kind === 'utility'
      ? selectedUtilityRecord.utilityBillUrl
      : selectedUtilityRecord.additionalAddressProofUrl;

    if (!documentUrl) {
      NotificationManager.error('No document URL available to verify.');
      return;
    }

    setDocVerifyLoading(prev => ({ ...prev, [kind]: true }));
    setDocVerifyError(prev => ({ ...prev, [kind]: null }));
    setDocVerifyResult(prev => ({ ...prev, [kind]: null }));

    try {
      const payload = {
        user_id: String(selectedUtilityRecord.userId ?? selectedUtilityRecord.id ?? ''),
        document_url: documentUrl,
        document_type: documentType,
      };
      const res = await httpPost(
        `${customBaseUrl.autoKycUrl}/document/api/v1/verify-document`,
        payload
      );

      if (res && (res.success === false || res.status === false)) {
        setDocVerifyError(prev => ({ ...prev, [kind]: res?.message || 'Verification failed.' }));
      } else if (res == null) {
        setDocVerifyError(prev => ({ ...prev, [kind]: 'No response from verification service.' }));
      } else {
        const data = res.data ?? res;
        setDocVerifyResult(prev => ({ ...prev, [kind]: data }));
      }
    } catch (e) {
      console.error('Document verification error:', e);
      setDocVerifyError(prev => ({ ...prev, [kind]: 'An error occurred while verifying the document.' }));
    } finally {
      setDocVerifyLoading(prev => ({ ...prev, [kind]: false }));
    }
  };

  const renderDocVerificationPanel = (kind) => {
    const loading = docVerifyLoading[kind];
    const error = docVerifyError[kind];
    const result = docVerifyResult[kind];
    const documentType = kind === 'utility' ? 'UTILITY_BILL' : 'ADDRESS_PROOF';
    const label = kind === 'utility' ? 'Utility Bill' : 'Additional Address Proof';

    let authenticityValue = null;
    let authenticityKey = null;
    if (result && typeof result === 'object') {
      const candidates = ['is_authentic', 'authentic', 'isAuthentic', 'valid', 'is_valid', 'verified'];
      for (const k of candidates) {
        if (k in result) { authenticityKey = k; authenticityValue = result[k]; break; }
      }
    }

    return (
      <div style={{ marginTop: 12, padding: 12, border: '1px dashed #d9d9d9', borderRadius: 6, background: '#fafafa' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <strong>{label} — Document Authenticity Check</strong>
          <Button
            type="primary"
            size="small"
            loading={loading}
            onClick={() => handleVerifyDocumentAuthenticity(kind, documentType)}
          >
            Validate Document Authenticity
          </Button>
        </div>

        {error && <Alert type="error" showIcon message="Verification Failed" description={error} />}

        {result && (
          <div style={{ marginTop: 8 }}>
            {authenticityKey && (
              <div style={{ marginBottom: 8 }}>
                <span style={{
                  fontWeight: 700,
                  padding: '4px 10px',
                  borderRadius: 4,
                  background: authenticityValue ? '#f6ffed' : '#fff1f0',
                  border: `1px solid ${authenticityValue ? '#b7eb8f' : '#ffa39e'}`,
                  color: authenticityValue ? '#52c41a' : '#ff4d4f',
                }}>
                  {authenticityValue ? 'AUTHENTIC' : 'NOT AUTHENTIC'}
                </span>
              </div>
            )}
            <Descriptions bordered column={1} size="small">
              {Object.entries(result).map(([k, v]) => {
                let display;
                if (v == null || v === '') display = 'N/A';
                else if (typeof v === 'boolean') display = v ? 'Yes' : 'No';
                else if (typeof v === 'object') {
                  display = (
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto' }}>
                      {JSON.stringify(v, null, 2)}
                    </pre>
                  );
                } else display = String(v);
                const labelStr = k.replace(/[_-]/g, ' ').replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim();
                return <Descriptions.Item key={k} label={labelStr}>{display}</Descriptions.Item>;
              })}
            </Descriptions>
          </div>
        )}
      </div>
    );
  };

  const handleUtilityAction = async (action, record) => {
    if (action === 'View Document') {
      // Reset validation state
      setMeterValidationResult(null);
      setMeterValidationError(null);
      setManualMeterNo(record?.accountNumber || '');
      setManualServiceType('');

      // Reset document verification state
      setDocVerifyLoading({ utility: false, additional: false });
      setDocVerifyResult({ utility: null, additional: null });
      setDocVerifyError({ utility: null, additional: null });

      // Reset geocoding state
      setResolvedAddress('');
      setResolveAddressError(null);
      setBillingAddressGeocode(null);
      setBillingAddressGeocodeError(null);
      setBackendComparisonResult(null);
      setBackendComparisonError(null);

      setSelectedUtilityRecord(record);
      setIsUtilityModalVisible(true);
      fetchElectricityProviders(record);

      if (record?.latitude && record?.longitude) {
        resolveGeolocationAddress(record.latitude, record.longitude);
      }
      if (record?.billingAddress) {
        geocodeBillingAddress(record.billingAddress, record.id);
      }
      return;
    }

    Swal.fire({
      title: `Enter remarks for ${action}ing Utility verification for User ID: ${record.userId}`,
      input: 'text',
      inputAttributes: {
        autocapitalize: 'off'
      },
      showCancelButton: true,
      confirmButtonText: 'Submit',
      showLoaderOnConfirm: true,
      preConfirm: (remarks) => {
        if (!remarks) {
          Swal.showValidationMessage(
            'Remarks cannot be empty!'
          );
        }
        return remarks;
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsUtilityActionLoading(true);
        const remarks = encodeURIComponent(result.value);
        // NOTE: The token is hardcoded as 'kk' as per the request. In a real application, this should be dynamically retrieved.
        const token = 'kk';
        try {
          let res;
          if (action === 'Approve') {
            const refLat = billingAddressGeocode?.lat ?? record?.latitude ?? '';
            const refLng = billingAddressGeocode?.lon ?? record?.longitude ?? '';
            res = await httpPost(`${customBaseUrl.authUrl}/api/v1/utility-bill-verifications/${record.id}/approve?remarks=${remarks}&token=${token}&refLat=${refLat}&refLng=${refLng}`);
          } else if (action === 'Reject') {
            res = await httpPost(`${customBaseUrl.authUrl}/api/v1/utility-bill-verifications/${record.id}/reject?remarks=${remarks}&token=${token}`);
          }

          if (res?.success) {
            NotificationManager.success(`${action}d Utility verification successfully!`);
            setIsUtilityModalVisible(false);
            fetchUtilityVerifications(); // Refresh the list
          } else {
            NotificationManager.error(res?.message || `Failed to ${action} Utility verification.`);
          }
        } catch (err) {
          NotificationManager.error(`An error occurred while ${action}ing Utility verification.`);
          console.error(err);
        } finally {
          setIsUtilityActionLoading(false);
        }
      }
    });
  };

  const handleCacAction = async (action, record) => {
    if (action === 'View Details') {
      setSelectedCacRecord(record);
      setIsCacModalVisible(true);
      return;
    }

    const result = await Swal.fire({
      title: `Enter remarks for ${action}ing CAC verification for ${record.businessName}`,
      input: 'text',
      inputAttributes: { autocapitalize: 'off' },
      showCancelButton: true,
      confirmButtonText: 'Submit',
      showLoaderOnConfirm: true,
      preConfirm: (remarks) => {
        if (!remarks) {
          Swal.showValidationMessage('Remarks cannot be empty!');
        }
        return remarks;
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (result.isConfirmed) {
      setIsCacActionLoading(true);
      const remarks = encodeURIComponent(result.value);
      const approver = localStorage.getItem('email');
      try {
        let res;
        if (action === 'Approve') {
          res = await httpPost(`${customBaseUrl.authUrl}/api/v1/cac-business-accounts/${record.id}/approve?approvedBy=${approver}`);
        } else if (action === 'Reject') {
          res = await httpPost(`${customBaseUrl.authUrl}/api/v1/cac-business-accounts/${record.id}/reject?rejectedBy=${approver}&rejectionReason=${remarks}`);
        }

        if (res?.success) {
          NotificationManager.success(`${action}d CAC verification successfully!`);
          fetchCacVerifications(); // Refresh the list
          setIsCacModalVisible(false);
        } else {
          NotificationManager.error(res?.message || `Failed to ${action} CAC verification.`);
        }
      } catch (err) {
        NotificationManager.error(`An error occurred while ${action}ing CAC verification.`);
        console.error(err);
      } finally {
        setIsCacActionLoading(false);
      }
    }
  };

  const ninColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'User ID', dataIndex: 'userId', key: 'userId' },
    { title: 'First Name', dataIndex: 'firstName', key: 'firstName' },
    { title: 'Middle Name', render: (text, record) => `${record.middleName || 'N/A'}` },
    { title: 'Last Name', dataIndex: 'lastName', key: 'lastName' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'NIN', dataIndex: 'nin', key: 'nin' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Admin Remarks', dataIndex: 'adminRemarks', key: 'adminRemarks' },
    { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt' },
    { title: 'Approved By', dataIndex: 'approvedBy', key: 'approvedBy' },
    { title: 'Approved At', dataIndex: 'approvedAt', key: 'approvedAt' },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <AntDropdown
          dropDownOptions={['View Details']}
          handleDropdownAction={(action) => handleNinAction(action, record)}
        />
      ),
    },
  ];

  const utilityColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'User ID', dataIndex: 'userId', key: 'userId' },
    {
      title: 'Customer Name',
      key: 'customerName',
      render: (text, record) => `${record.userFirstName || ''} ${record.userLastName || ''}`.trim() || 'N/A'
    },
    { title: 'Phone Number', dataIndex: 'userPhoneNumber', key: 'userPhoneNumber' },
    { title: 'Verification Type', dataIndex: 'verificationType', key: 'verificationType', render: (text) => text || 'N/A' },
    { title: 'Bill Type', dataIndex: 'utilityBillType', key: 'utilityBillType' },
    { title: 'Provider', dataIndex: 'utilityProvider', key: 'utilityProvider' },
    { title: 'Account Number', dataIndex: 'accountNumber', key: 'accountNumber' },
    { title: 'Billing Address', dataIndex: 'billingAddress', key: 'billingAddress' },
    { title: "Within Range", dataIndex: 'rangeWithinRange', render: (tx, rcd) => rcd?.rangeWithinRange ? 'TRUE' : rcd?.rangeWithinRange === null ? 'N/A' : 'FALSE' },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Admin Remarks', dataIndex: 'adminRemarks', key: 'adminRemarks' },
    { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt' },
    { title: 'Approved By', dataIndex: 'approvedBy', key: 'approvedBy' },
    { title: 'Approved At', dataIndex: 'approvedAt', key: 'approvedAt' },
    { title: 'Rejected By', dataIndex: 'rejectedBy', key: 'rejectedBy' },
    { title: 'Rejected At', dataIndex: 'rejectedAt', key: 'rejectedAt' },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <AntDropdown
          dropDownOptions={['View Document']}
          handleDropdownAction={(action) => handleUtilityAction(action, record)}
        />
      ),
    },
  ];

  const cacColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'User ID', dataIndex: 'userId', key: 'userId' },
    { title: 'Business Name', dataIndex: 'businessName', key: 'businessName' },
    { title: 'Reg Number', dataIndex: 'registrationNumber', key: 'registrationNumber' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phoneNo', key: 'phoneNo' },
    { title: 'Status', dataIndex: 'accountStatus', key: 'accountStatus' },
    { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt' },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <AntDropdown
          dropDownOptions={['View Details']}
          handleDropdownAction={(action) => handleCacAction(action, record)}
        />
      ),
    },
  ];

  return (
    <Layout>
      <Card title="Manage KYC Verifications">
        <Tabs defaultActiveKey="nin" onChange={setActiveTab}>
          <TabPane tab="Face Verification" key="nin">
            {ninLoading ? (
              <Spin tip="Loading face verifications..." />
            ) : ninError ? (
              <Alert message="Error" description={ninError} type="error" showIcon />
            ) : (
              <>
                <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Input
                    placeholder="Search by name, email, NIN or phone number..."
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    value={ninSearchText}
                    onChange={(e) => setNinSearchText(e.target.value)}
                    allowClear
                    style={{ maxWidth: 300 }}
                  />
                  <Select
                    value={ninStatus}
                    style={{ width: 140 }}
                    onChange={(val) => setNinStatus(val)}
                  >
                    <Select.Option value="PENDING">Pending</Select.Option>
                    <Select.Option value="UNDER_REVIEW">Under Review</Select.Option>
                    <Select.Option value="APPROVED">Approved</Select.Option>
                    <Select.Option value="REJECTED">Rejected</Select.Option>
                  </Select>
                  <Input
                    placeholder="Approved By (Admin Email)"
                    value={ninApprovedBy}
                    onChange={(e) => setNinApprovedBy(e.target.value)}
                    allowClear
                    style={{ maxWidth: 220 }}
                  />
                  <DatePicker
                    placeholder="From Date"
                    format="YYYY-MM-DD"
                    value={ninFromDate}
                    onChange={(date) => setNinFromDate(date)}
                    style={{ width: 140 }}
                  />
                  <DatePicker
                    placeholder="To Date"
                    format="YYYY-MM-DD"
                    value={ninToDate}
                    onChange={(date) => setNinToDate(date)}
                    style={{ width: 140 }}
                  />
                  <Button type="primary" onClick={() => fetchNinVerifications(ninStatus)}>
                    Apply Filters
                  </Button>
                  <Button onClick={() => {
                    setNinSearchText('');
                    setNinStatus('PENDING');
                    setNinApprovedBy('');
                    setNinFromDate(null);
                    setNinToDate(null);
                    fetchNinVerifications('PENDING', {}, true);
                  }}>
                    Reset
                  </Button>
                </div>
                <AntTable
                  pagination={ninPagination}
                  users={filteredNinVerifications}
                  loading={ninLoading}
                  handleGetNext={(params) => fetchNinVerifications(ninStatus, params)}
                  columns={ninColumns}
                  tableScrollWidth={1800}
                />
              </>
            )}
          </TabPane>
          <TabPane tab="Utility Verification" key="utility">
            {utilityLoading ? (
              <Spin tip="Loading Utility verifications..." />
            ) : utilityError ? (
              <Alert message="Error" description={utilityError} type="error" showIcon />
            ) : (
              <>
                <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Input
                    placeholder="Search by name, email, account or phone number..."
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    value={utilitySearchText}
                    onChange={(e) => setUtilitySearchText(e.target.value)}
                    allowClear
                    style={{ maxWidth: 300 }}
                  />
                  <Select
                    value={utilityStatus}
                    style={{ width: 140 }}
                    onChange={(val) => setUtilityStatus(val)}
                  >
                    <Select.Option value="PENDING">Pending</Select.Option>
                    <Select.Option value="UNDER_REVIEW">Under Review</Select.Option>
                    <Select.Option value="APPROVED">Approved</Select.Option>
                    <Select.Option value="REJECTED">Rejected</Select.Option>
                  </Select>
                  <Input
                    placeholder="Approved By (Admin Email)"
                    value={utilityApprovedBy}
                    onChange={(e) => setUtilityApprovedBy(e.target.value)}
                    allowClear
                    style={{ maxWidth: 220 }}
                  />
                  <DatePicker
                    placeholder="From Date"
                    format="YYYY-MM-DD"
                    value={utilityFromDate}
                    onChange={(date) => setUtilityFromDate(date)}
                    style={{ width: 140 }}
                  />
                  <DatePicker
                    placeholder="To Date"
                    format="YYYY-MM-DD"
                    value={utilityToDate}
                    onChange={(date) => setUtilityToDate(date)}
                    style={{ width: 140 }}
                  />
                  <Button type="primary" onClick={() => fetchUtilityVerifications(utilityStatus)}>
                    Apply Filters
                  </Button>
                  <Button onClick={() => {
                    setUtilitySearchText('');
                    setUtilityStatus('PENDING');
                    setUtilityApprovedBy('');
                    setUtilityFromDate(null);
                    setUtilityToDate(null);
                    fetchUtilityVerifications('PENDING', {}, true);
                  }}>
                    Reset
                  </Button>
                </div>
                <AntTable
                  pagination={utilityPagination}
                  users={filteredUtilityVerifications}
                  loading={utilityLoading}
                  handleGetNext={(params) => fetchUtilityVerifications(utilityStatus, params)}
                  columns={utilityColumns}
                  tableScrollWidth={1800}
                />
              </>
            )}
          </TabPane>
          <TabPane tab="CAC Verification" key="cac">
            {cacLoading ? (
              <Spin tip="Loading CAC verifications..." />
            ) : cacError ? (
              <Alert message="Error" description={cacError} type="error" showIcon />
            ) : (
              <>
                <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Input
                    placeholder="Search by business name, reg no, email or phone..."
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    value={cacSearchText}
                    onChange={(e) => setCacSearchText(e.target.value)}
                    allowClear
                    style={{ maxWidth: 300 }}
                  />
                  <Select
                    value={cacStatus}
                    style={{ width: 140 }}
                    onChange={(val) => setCacStatus(val)}
                  >
                    <Select.Option value="PENDING">Pending</Select.Option>
                    <Select.Option value="UNDER_REVIEW">Under Review</Select.Option>
                    <Select.Option value="APPROVED">Approved</Select.Option>
                    <Select.Option value="REJECTED">Rejected</Select.Option>
                  </Select>
                  <Input
                    placeholder="Approved By (Admin Email)"
                    value={cacApprovedBy}
                    onChange={(e) => setCacApprovedBy(e.target.value)}
                    allowClear
                    style={{ maxWidth: 220 }}
                  />
                  <DatePicker
                    placeholder="From Date"
                    format="YYYY-MM-DD"
                    value={cacFromDate}
                    onChange={(date) => setCacFromDate(date)}
                    style={{ width: 140 }}
                  />
                  <DatePicker
                    placeholder="To Date"
                    format="YYYY-MM-DD"
                    value={cacToDate}
                    onChange={(date) => setCacToDate(date)}
                    style={{ width: 140 }}
                  />
                  <Button type="primary" onClick={() => fetchCacVerifications(cacStatus)}>
                    Apply Filters
                  </Button>
                  <Button onClick={() => {
                    setCacSearchText('');
                    setCacStatus('PENDING');
                    setCacApprovedBy('');
                    setCacFromDate(null);
                    setCacToDate(null);
                    fetchCacVerifications('PENDING', {}, true);
                  }}>
                    Reset
                  </Button>
                </div>
                <AntTable
                  pagination={cacPagination}
                  users={filteredCacVerifications}
                  loading={cacLoading}
                  handleGetNext={(params) => fetchCacVerifications(cacStatus, params)}
                  columns={cacColumns}
                  tableScrollWidth={1200}
                />
              </>
            )}
          </TabPane>
          <TabPane tab="Refresh BVN" key="bvn">
            <RefreshBvnForm />
          </TabPane>
        </Tabs>
      </Card>
      <Modal
        title="Utility Bill Details & Verification"
        visible={isUtilityModalVisible}
        onCancel={() => { setIsUtilityModalVisible(false); setMeterValidationResult(null); setMeterValidationError(null); setManualMeterNo(''); setManualServiceType(''); setElectricityProviders([]); setResolvedAddress(''); setResolveAddressError(null); setBillingAddressGeocode(null); setBillingAddressGeocodeError(null); setBackendComparisonResult(null); setBackendComparisonError(null); setDocVerifyLoading({ utility: false, additional: false }); setDocVerifyResult({ utility: null, additional: null }); setDocVerifyError({ utility: null, additional: null }); }}
        footer={[
          <Button key="close" onClick={() => { setIsUtilityModalVisible(false); setMeterValidationResult(null); setMeterValidationError(null); setManualMeterNo(''); setManualServiceType(''); setElectricityProviders([]); setResolvedAddress(''); setResolveAddressError(null); setBillingAddressGeocode(null); setBillingAddressGeocodeError(null); setBackendComparisonResult(null); setBackendComparisonError(null); setDocVerifyLoading({ utility: false, additional: false }); setDocVerifyResult({ utility: null, additional: null }); setDocVerifyError({ utility: null, additional: null }); }} disabled={isUtilityActionLoading}>
            Close
          </Button>,
          selectedUtilityRecord?.status !== 'APPROVED' && (
            <Button
              key="reject"
              danger
              loading={isUtilityActionLoading}
              onClick={() => handleUtilityAction('Reject', selectedUtilityRecord)}
            >
              Reject
            </Button>
          ),
          selectedUtilityRecord?.status !== 'APPROVED' && (
            <Button
              key="approve"
              type="primary"
              loading={isUtilityActionLoading}
              onClick={() => handleUtilityAction('Approve', selectedUtilityRecord)}
            >
              Approve
            </Button>
          ),
        ]}
        width={900}
      >
        {selectedUtilityRecord && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Submitted Details Section */}
            <Card title="Submitted Utility Bill Details" size="small" type="inner">
              <Descriptions column={2} size="small" layout="horizontal" bordered>
                <Descriptions.Item label="Customer Name">
                  {`${selectedUtilityRecord.userFirstName || ''} ${selectedUtilityRecord.userLastName || ''}`.trim() || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Phone Number">
                  {selectedUtilityRecord.userPhoneNumber || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Verification Type">
                  {selectedUtilityRecord.verificationType || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Bill Type">
                  {selectedUtilityRecord.utilityBillType || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Provider">
                  {selectedUtilityRecord.utilityProvider || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Account Number">
                  {selectedUtilityRecord.accountNumber || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Verification Status">
                  {selectedUtilityRecord.status || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Billing Address" span={2}>
                  <span style={{ fontWeight: '600', color: '#1890ff' }}>
                    {selectedUtilityRecord.billingAddress || 'N/A'}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Remarks" span={2}>
                  {selectedUtilityRecord.remarks || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Document URL" span={2}>
                  {selectedUtilityRecord.utilityBillUrl ? (
                    <a href={selectedUtilityRecord.utilityBillUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', fontWeight: '500' }}>
                      {selectedUtilityRecord.utilityBillUrl}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </Descriptions.Item>
                {selectedUtilityRecord.additionalAddressProofUrl && (
                  <Descriptions.Item label="Additional Address Proof URL" span={2}>
                    <a href={selectedUtilityRecord.additionalAddressProofUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', fontWeight: '500' }}>
                      {selectedUtilityRecord.additionalAddressProofUrl}
                    </a>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Captured Geolocation Details Section */}
            {selectedUtilityRecord && (selectedUtilityRecord.latitude || selectedUtilityRecord.longitude) && (
              <Card title="Captured Geolocation Details" size="small" type="inner">
                <Descriptions column={2} size="small" layout="horizontal" bordered>
                  <Descriptions.Item label="Coordinates">
                    {selectedUtilityRecord.latitude && selectedUtilityRecord.longitude ? (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${selectedUtilityRecord.latitude},${selectedUtilityRecord.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: '500' }}
                      >
                        {selectedUtilityRecord.latitude}, {selectedUtilityRecord.longitude} ↗ (View on Google Maps)
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Location Accuracy">
                    {selectedUtilityRecord.locationAccuracyM
                      ? `${Number(selectedUtilityRecord.locationAccuracyM).toFixed(2)} meters`
                      : 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Device ID">
                    {selectedUtilityRecord.locationDeviceId || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Captured At">
                    {selectedUtilityRecord.locationCapturedAt
                      ? dayjs(selectedUtilityRecord.locationCapturedAt).format('YYYY-MM-DD HH:mm:ss')
                      : 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Resolved Address" span={2}>
                    {isResolvingAddress ? (
                      <Spin size="small" tip="Resolving address..." />
                    ) : resolveAddressError ? (
                      <span style={{ color: '#faad14' }}>{resolveAddressError}</span>
                    ) : resolvedAddress ? (
                      <span style={{ fontWeight: '600', color: '#52c41a' }}>
                        {resolvedAddress}
                      </span>
                    ) : (
                      'No address resolved'
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {/* Geocode Comparison Section */}
            {selectedUtilityRecord && selectedUtilityRecord.billingAddress && (
              <Card title="Billing Address vs Sent Location Geocode Comparison" size="small" type="inner">
                {isGeocodingBillingAddress ? (
                  <div style={{ textAlign: 'center', padding: '12px 0' }}>
                    <Spin size="small" tip="Geocoding billing address..." />
                  </div>
                ) : billingAddressGeocodeError ? (
                  <Alert message="Geocoding Failed" description={billingAddressGeocodeError} type="warning" showIcon />
                ) : billingAddressGeocode ? (
                  <div>
                    <Descriptions column={2} size="small" layout="horizontal" bordered>
                      <Descriptions.Item label="Submitted Billing Address" span={2}>
                        <span style={{ fontWeight: '500' }}>{selectedUtilityRecord.billingAddress}</span>
                      </Descriptions.Item>
                      <Descriptions.Item label="Billing Address Geocode">
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${billingAddressGeocode.lat},${billingAddressGeocode.lon}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontWeight: '500' }}
                        >
                          {billingAddressGeocode.lat.toFixed(6)}, {billingAddressGeocode.lon.toFixed(6)} ↗
                        </a>
                      </Descriptions.Item>
                      <Descriptions.Item label="Sent Location Geocode">
                        {selectedUtilityRecord.latitude && selectedUtilityRecord.longitude ? (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${selectedUtilityRecord.latitude},${selectedUtilityRecord.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontWeight: '500' }}
                          >
                            {Number(selectedUtilityRecord.latitude).toFixed(6)}, {Number(selectedUtilityRecord.longitude).toFixed(6)} ↗
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Location Range Match">
                        {isBackendComparing ? (
                          <Spin size="small" tip="Comparing..." />
                        ) : backendComparisonError ? (
                          <span style={{ color: '#ff4d4f' }}>{backendComparisonError}</span>
                        ) : backendComparisonResult ? (
                          (() => {
                            const isWithinRange = backendComparisonResult.withinRange === true;
                            return (
                              <span style={{
                                fontWeight: '700',
                                color: isWithinRange ? '#52c41a' : '#ff4d4f',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                background: isWithinRange ? '#f6ffed' : '#fff1f0',
                                border: `1px solid ${isWithinRange ? '#b7eb8f' : '#ffa39e'}`
                              }}>
                                {isWithinRange ? 'MATCHED (Within Range)' : 'MISMATCH (Out of Range)'}
                              </span>
                            );
                          })()
                        ) : (
                          'N/A'
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Distance Difference">
                        {isBackendComparing ? (
                          <Spin size="small" />
                        ) : backendComparisonResult?.distanceMeters !== undefined ? (
                          (() => {
                            const meters = backendComparisonResult.distanceMeters;
                            return meters < 1000
                              ? `${meters.toFixed(2)} meters`
                              : `${(meters / 1000).toFixed(2)} km`;
                          })()
                        ) : (
                          'N/A'
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Max Allowed Threshold">
                        {backendComparisonResult?.maxAllowedMeters !== undefined
                          ? `${backendComparisonResult.maxAllowedMeters} meters`
                          : 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Threshold Validation">
                        {backendComparisonResult?.thresholdEnabled !== undefined
                          ? (backendComparisonResult.thresholdEnabled ? 'Enabled' : 'Disabled')
                          : 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Resolved Address Info" span={2}>
                        <span style={{ fontSize: '11px', color: '#8c8c8c' }}>
                          {billingAddressGeocode.display_name}
                        </span>
                      </Descriptions.Item>
                    </Descriptions>
                  </div>
                ) : (
                  <Alert message="No Address Geocode" description="Billing address could not be resolved." type="info" showIcon />
                )}
              </Card>
            )}

            {/* Meter Validation */}
            <Card title="Meter / Account Validation" size="small" type="inner">
              {/* Input form */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
                <div style={{ flex: '1 1 240px' }}>
                  <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Provider</div>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select electricity provider"
                    value={manualServiceType || undefined}
                    onChange={(val) => setManualServiceType(val)}
                    showSearch
                    optionFilterProp="children"
                    loading={electricityProviders.length === 0}
                    notFoundContent={electricityProviders.length === 0 ? <Spin size="small" /> : 'No providers found'}
                  >
                    {electricityProviders.map((p) => (
                      <Select.Option key={p.service_type} value={p.service_type}>
                        {p.name || p.shortname || p.service_type}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Meter / Account Number</div>
                  <Input
                    placeholder="Enter meter number"
                    value={manualMeterNo}
                    onChange={(e) => setManualMeterNo(e.target.value)}
                    allowClear
                  />
                </div>
                <Button
                  type="primary"
                  loading={isMeterValidating}
                  disabled={!manualMeterNo || !manualServiceType}
                  onClick={() => validateMeterNumber(manualMeterNo, manualServiceType)}
                  style={{ flexShrink: 0 }}
                >
                  Validate
                </Button>
              </div>

              {/* Results */}
              {isMeterValidating ? (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <Spin tip="Validating meter number..." />
                </div>
              ) : meterValidationError ? (
                <Alert
                  message="Validation Failed"
                  description={meterValidationError}
                  type="warning"
                  showIcon
                />
              ) : meterValidationResult ? (
                <Descriptions column={2} size="small" layout="horizontal" bordered>
                  <Descriptions.Item label="Validated Name">
                    <span style={{ fontWeight: 600, color: '#52c41a' }}>
                      {meterValidationResult.name || 'N/A'}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Account Number">
                    {meterValidationResult.accountNumber || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Validated Address" span={2}>
                    <span style={{ fontWeight: 600, color: '#1890ff' }}>
                      {meterValidationResult.address || 'N/A'}
                    </span>
                  </Descriptions.Item>
                  {meterValidationResult.outstandingBalance !== null &&
                    meterValidationResult.outstandingBalance !== undefined && (
                      <Descriptions.Item label="Outstanding Balance">
                        {meterValidationResult.outstandingBalance}
                      </Descriptions.Item>
                    )}
                  {/* {meterValidationResult.minimumAmount !== null &&
                    meterValidationResult.minimumAmount !== undefined && (
                      <Descriptions.Item label="Minimum Amount">
                        {meterValidationResult.minimumAmount}
                      </Descriptions.Item>
                    )} */}
                </Descriptions>
              ) : null}
            </Card>

            {/* Utility Bill Document Preview */}
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
              <h3 style={{ textAlign: 'center', marginBottom: 8 }}>Utility Bill Document</h3>
              {selectedUtilityRecord.utilityBillUrl && (
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <a href={selectedUtilityRecord.utilityBillUrl} target="_blank" rel="noopener noreferrer" style={{ fontWeight: '500' }}>
                    Open Document in New Tab ↗
                  </a>
                </div>
              )}
              <div style={{ textAlign: 'center' }}>
                {selectedUtilityRecord.utilityBillUrl ? (
                  selectedUtilityRecord.utilityBillUrl.split('?')[0].toLowerCase().endsWith('.pdf') ? (
                    <iframe
                      src={selectedUtilityRecord.utilityBillUrl}
                      title="Utility Bill Document"
                      style={{ width: '100%', height: '550px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  ) : (
                    <img
                      src={selectedUtilityRecord.utilityBillUrl}
                      alt="Utility Bill"
                      style={{ maxWidth: '100%', maxHeight: '600px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  )
                ) : (
                  <Alert message="No Document" description="No utility bill document URL has been uploaded." type="warning" showIcon />
                )}
              </div>
              {selectedUtilityRecord.utilityBillUrl && renderDocVerificationPanel('utility')}
            </div>

            {/* Additional Address Proof Document Preview */}
            {selectedUtilityRecord.additionalAddressProofUrl && (
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16, marginTop: 16 }}>
                <h3 style={{ textAlign: 'center', marginBottom: 8 }}>Additional Address Proof Document</h3>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <a href={selectedUtilityRecord.additionalAddressProofUrl} target="_blank" rel="noopener noreferrer" style={{ fontWeight: '500' }}>
                    Open Additional Address Proof in New Tab ↗
                  </a>
                </div>
                <div style={{ textAlign: 'center' }}>
                  {selectedUtilityRecord.additionalAddressProofUrl.split('?')[0].toLowerCase().endsWith('.pdf') ? (
                    <iframe
                      src={selectedUtilityRecord.additionalAddressProofUrl}
                      title="Additional Address Proof Document"
                      style={{ width: '100%', height: '550px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  ) : (
                    <img
                      src={selectedUtilityRecord.additionalAddressProofUrl}
                      alt="Additional Address Proof"
                      style={{ maxWidth: '100%', maxHeight: '600px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  )}
                </div>
                {renderDocVerificationPanel('additional')}
              </div>
            )}
          </div>
        )}
      </Modal>
      <Modal
        title="CAC Account Details"
        visible={isCacModalVisible}
        onCancel={() => setIsCacModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsCacModalVisible(false)} disabled={isCacActionLoading}>
            Close
          </Button>,
          selectedCacRecord?.accountStatus !== 'APPROVED' && (
            <Button
              key="reject"
              danger
              loading={isCacActionLoading}
              onClick={() => handleCacAction('Reject', selectedCacRecord)}
            >
              Reject
            </Button>
          ),
          selectedCacRecord?.accountStatus !== 'APPROVED' && (
            <Button
              key="approve"
              type="primary"
              loading={isCacActionLoading}
              onClick={() => handleCacAction('Approve', selectedCacRecord)}
            >
              Approve
            </Button>
          ),
        ]}
        width={1100}
      >
        {selectedCacRecord && (
          <Descriptions bordered column={2} size="small">
            {Object.entries(selectedCacRecord).map(([key, value]) => (
              <Descriptions.Item key={key} label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}>
                {typeof value === 'string' && value.startsWith('http') ? (
                  // <a href={value} target="_blank" rel="noopener noreferrer"></a>
                  <a href={value} target="_blank" rel="noopener noreferrer">View</a>
                ) : (
                  value ? String(value) : 'N/A'
                )}
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Modal>
    </Layout>
  );
};

export default ManageKyc;